import { object, string, array, bool } from 'prop-types';
import TrackingHelper from 'src/utils/trackingHelper';
import { QUICK_LINKS_PAGE_URL } from 'src/constants/urlConstants';
import { TrackedLink } from './trackedLink';
import { UserCard } from './userCard';
import { MobileMenu } from './mobileMenu';
import { HeaderActions } from './headerActions';
import {
  headerActionsList,
  getDropdownActionsList,
} from './headerActions/headerActionsHelper';
import { HeaderLogo } from './HeaderLogo';
import {
  StyledSubAgentHeader,
  StyledInnerContainer,
  StyledActionsDropdown,
  StyledActionsContainer,
  StyledAction,
  StyledActionLabel,
  StyledUserCardContainer,
  StyledActionButtonWrapper,
  StyledIcon,
} from './styles';
import { baseUrl, backendApiUrl, logoUrl } from 'src/utils/capabilities';
import { get } from 'lodash';

const configureActions = (actionsConfig, actions) => {
  if (Array.isArray(actions)) {
    return actions.reduce((resultActions, action) => {
      const actionAttrs = actionsConfig[action.name];

      if (actionAttrs) {
        resultActions.push(
          Object.assign({}, actionAttrs, {
            link: action.url,
            external: action.url.includes('://'),
          }),
        );
      }

      return resultActions;
    }, []);
  }
  return [];
};

const headerCustomColorsMapping = {
  cw: {
    default: '#033167',
    dark: '#032042',
    light: '#043d80',
  },
};

export const SubAgentHeader = ({
  impersonatedUser,
  logoutUrl,
  originalUser,
  mainActions,
  secondaryActions,
  isAgencyStatusApproved,
}) => {
  const { t } = window.translation;

  const headerCustomColor =
    get(headerCustomColorsMapping, window.config.themeId) || {};

  const dropdownActions = getDropdownActionsList(logoutUrl);

  const configuredMainActions = configureActions(
    headerActionsList,
    mainActions || [],
  );
  const configuredSecondaryActions = configureActions(
    headerActionsList,
    secondaryActions || [],
  );

  const mobileActions = [
    ...configuredMainActions,
    ...configuredSecondaryActions,
    ...dropdownActions,
  ];

  const finishImpersonationUrl = `${backendApiUrl}/admin/impersonates/finish`;

  const userCardData = {
    actionUrl: finishImpersonationUrl || `${baseUrl}${QUICK_LINKS_PAGE_URL}`,
    label: finishImpersonationUrl
      ? t('subAgentHeader.endImpersonation')
      : t('subAgentHeader.toDashboard'),
  };

  const trackActionClick = action => {
    TrackingHelper.track('LINK_CLICKED', {
      to: action.link,
      label: action.label,
    });
  };

  const impersonatedUserCard = impersonatedUser && (
    <UserCard
      headerCustomColor={headerCustomColor}
      dataTestId="impersonatedUser"
      title={impersonatedUser.name}
      description={impersonatedUser.businessName}
      isPrimary
    >
      <div>
        <StyledActionButtonWrapper>
          <TrackedLink href={userCardData.actionUrl}>
            {userCardData.label}
          </TrackedLink>
        </StyledActionButtonWrapper>
      </div>
    </UserCard>
  );

  return (
    <StyledSubAgentHeader backgroundColor={headerCustomColor?.default}>
      <StyledUserCardContainer>
        <MobileMenu
          actions={mobileActions}
          title={originalUser.name}
          subtitle={originalUser.businessName}
        >
          {impersonatedUserCard}
        </MobileMenu>
        <StyledInnerContainer>
          <HeaderLogo
            isAgencyStatusApproved={isAgencyStatusApproved}
            logoUrl={logoUrl}
            t={t}
          />
          {configuredMainActions.map(a => (
            <StyledAction
              to={a.link}
              label={a.label}
              external={a.external}
              key={a.label}
              onClick={() => {
                trackActionClick(a);
              }}
              dataTest="sso-header__action"
            >
              <StyledIcon icon={a.icon} />
              <StyledActionLabel>{a.label}</StyledActionLabel>
            </StyledAction>
          ))}
        </StyledInnerContainer>
        {impersonatedUser && (
          <StyledInnerContainer mobileHidden>
            {impersonatedUserCard}
          </StyledInnerContainer>
        )}
        <StyledActionsContainer>
          <div>
            {configuredSecondaryActions.map(a => (
              <StyledAction
                to={a.link}
                key={a.label}
                onClick={() => {
                  trackActionClick(a);
                }}
                dataTest="sso-header__action"
              >
                <StyledIcon icon={a.icon} />
                <StyledActionLabel>{a.label}</StyledActionLabel>
              </StyledAction>
            ))}
          </div>
          <StyledActionsDropdown data-test="sso-header__actions-dropdown">
            <HeaderActions
              headerCustomColor={headerCustomColor}
              actions={dropdownActions}
              title={originalUser.name}
              subtitle={originalUser.businessName}
            />
          </StyledActionsDropdown>
        </StyledActionsContainer>
      </StyledUserCardContainer>
    </StyledSubAgentHeader>
  );
};

SubAgentHeader.propTypes = {
  originalUser: object.isRequired,
  impersonatedUser: object,
  mainActions: array,
  secondaryActions: array,
  logoutUrl: string,
  isAgencyStatusApproved: bool,
};
